import { BarChartXAxisLabel } from '@modules/charts/components/BarChartWrapper';
import { BarChartYAxisLabel } from '@modules/charts/components/BarChartWrapper/BarChartYAxisLabel';
import {
  BarGroup,
  BAR_LABEL_TYPOGRAPHY_VARIANT,
} from '@modules/charts/components/BarChartWrapper/BarGroup';
import {
  BarChartLabelWidthUnit,
  BarChartXAxisProps,
  BarChartYAxisProps,
  RenderBarParams,
  RenderChartTooltipParams,
  TooltipPosition,
} from '@modules/charts/types';
import { getChartLabelIdOrName } from '@modules/charts/utils';
import { GetChartLabelOptions } from '@modules/common/types';
import { getTextWidth } from '@modules/common/utils';
import { Box, Stack, SxProps, Theme } from '@mui/material';
import { useTheme } from '@mui/system';
import { useState } from 'react';
import { useMeasure } from 'react-use';

interface BarChartWrapperProps<T> {
  data: T[];
  xAxis: BarChartXAxisProps<T>;
  yAxis: BarChartYAxisProps<T>;
  minHeight?: number;
  renderTooltipContent?: (params: RenderChartTooltipParams) => JSX.Element;
  renderBar?: (params: RenderBarParams<T>) => React.ReactNode;
  getChartColor?: (index: number, sentiment: string) => string;
  getLabelOptions?: GetChartLabelOptions;
  shouldDisableInteraction?: boolean;
  canBeResized?: boolean;
  sxProps?: SxProps<Theme>;
  defaultLabelWidth?: number;
  defaultLabelWidthUnits?: BarChartLabelWidthUnit;
  onResizeComplete?: (labelWidthPercentage: number) => void;
  formatBarValueLabel?: (value: number) => string;
  shouldTakeFullHeight?: boolean;
  dataRoleAttribute?: string;
}

export const BarChartWrapper = <
  T extends {
    id: string;
  },
>({
  data,
  minHeight = 300,
  yAxis,
  xAxis,
  renderBar,
  renderTooltipContent,
  getChartColor,
  getLabelOptions,
  shouldDisableInteraction = false,
  canBeResized = true,
  sxProps,
  defaultLabelWidth,
  defaultLabelWidthUnits = 'pixels',
  onResizeComplete,
  formatBarValueLabel,
  shouldTakeFullHeight = false,
  dataRoleAttribute = 'bar-chart-wrapper',
}: BarChartWrapperProps<T>) => {
  const theme: Theme = useTheme();

  const [showTooltip, setShowTooltip] = useState(false);
  const [tooltipDataIndex, setTooltipDataIndex] = useState<number | null>(null);
  const [tooltipPosition, setTooltipPosition] =
    useState<TooltipPosition | null>(null);

  const [barGroupRef, { width: barGroupWidth }] = useMeasure();

  const defaultLabelWidthInPixels = (() => {
    const labelWidthFromCharLength = data.reduce((max, item) => {
      const labelText = getChartLabelIdOrName(String(item[yAxis.dataKey]));
      const computedLabelTextWidth = getTextWidth({
        text: labelText,
        fontSize:
          theme.typography[BAR_LABEL_TYPOGRAPHY_VARIANT].fontSize?.toString() ??
          '',
      });

      if (!computedLabelTextWidth) {
        return 300; // This should never happen, just a fallback
      }

      const buffer = xAxis.getLabelWidthBuffer
        ? xAxis.getLabelWidthBuffer(item)
        : 0;
      const labelLength = computedLabelTextWidth + buffer + 26; // 26 is extra buffer for spacing
      return Math.max(max, labelLength);
    }, 0);

    if (defaultLabelWidth && defaultLabelWidthUnits === 'pixels') {
      return defaultLabelWidth > labelWidthFromCharLength
        ? defaultLabelWidth
        : labelWidthFromCharLength;
    }

    if (defaultLabelWidth && defaultLabelWidthUnits === 'percentage') {
      const labelWidthFromPercentage =
        barGroupWidth * (defaultLabelWidth / 100);
      return labelWidthFromPercentage > labelWidthFromCharLength
        ? labelWidthFromPercentage
        : labelWidthFromCharLength;
    }

    return labelWidthFromCharLength;
  })();

  const onMouseEnter = (event: React.MouseEvent, index: number) => {
    if (shouldDisableInteraction) {
      return;
    }
    setShowTooltip(true);
    setTooltipDataIndex(index);
    setTooltipPosition({
      clientX: event.clientX,
      clientY: event.clientY,
    });
  };

  const onMouseLeave = () => {
    if (shouldDisableInteraction) {
      return;
    }
    setShowTooltip(false);
  };

  return (
    <Box
      sx={{
        pt: 1,
        pb: 2,
        px: 4,
        my: 'auto',
        height: '100%',
        ...sxProps,
      }}
      minHeight={shouldTakeFullHeight ? undefined : minHeight}
      height={shouldTakeFullHeight ? '100%' : undefined}
      data-role={dataRoleAttribute}
    >
      {!shouldDisableInteraction &&
        renderTooltipContent?.({
          showTooltip,
          dataIndex: tooltipDataIndex,
          tooltipPosition,
          setShowTooltip,
          setTooltipPosition,
        })}
      <Stack
        rowGap={2}
        height="100%"
        width="100%"
        alignItems="center"
        justifyContent="center"
      >
        <Stack
          direction="row"
          alignItems="center"
          columnGap={3}
          width="100%"
          height="calc(100% - 20px)" // 20px is the height of the x-axis label
        >
          {yAxis.label && <BarChartYAxisLabel label={yAxis.label} />}
          <Box ref={barGroupRef} width="100%" height="100%" minWidth={0}>
            <BarGroup
              data={data}
              minHeight={shouldTakeFullHeight ? undefined : minHeight}
              yAxis={yAxis}
              xAxis={xAxis}
              renderBar={renderBar}
              getChartColor={getChartColor}
              getLabelOptions={getLabelOptions}
              canBeResized={canBeResized}
              defaultLabelWidth={defaultLabelWidthInPixels}
              onMouseEnter={onMouseEnter}
              onMouseLeave={onMouseLeave}
              onResizeComplete={onResizeComplete}
              formatBarValueLabel={formatBarValueLabel}
            />
          </Box>
        </Stack>
        {xAxis.label && <BarChartXAxisLabel label={xAxis.label} />}
      </Stack>
    </Box>
  );
};
